import { NutriScore } from 'api/baseIngredient/GetBaseIngredientIdV1';
import * as apiPost from 'api/ingredient/PostIngredientV1';
import * as apiPut from 'api/ingredient/PutIngredientIdV1';
import { Characteristic } from 'enums/characteristic';
import { MEDIACOLLECTION } from 'enums/mediaCollection';
import { NutriScoreCategory } from 'enums/nutriScoreCategory';
import PERMISSIONS from 'enums/permissions';
import { arePermissionsInUserPermissions } from 'functions/tokenFunctions';
import { uploadMedia } from 'functions/uploadMedia';
import * as Co2Label from 'types/BaseIngredient/Detail/Co2Label';
import * as Co2List from 'types/BaseIngredient/Detail/Co2List';
import * as Image from 'types/BaseIngredient/Detail/Image';
import * as AllergenList from 'types/Ingredient/Detail/AllergenList';
import * as Ingredient from 'types/Ingredient/Detail/Ingredient';
import * as LockedAttributes from 'types/Ingredient/Detail/LockedAttributes';
import * as NutrientList from 'types/Ingredient/Detail/NutrientList';
import * as OrderGuide from 'types/Ingredient/Detail/OrderGuide/OrderGuide';
import * as SynonymList from 'types/Ingredient/Detail/SynonymList';
import * as UnitWeightList from 'types/Ingredient/Detail/UnitWeightList';
import * as IngredientProductList from 'types/Product/IngredientProductList';
import * as IngredientCategoryList from 'types/_general/Store/IngredientCategoryList';
import * as TagList from 'types/_general/Store/TagList';
import * as Translation from 'types/_general/Translation';

export type Type = {
	id: string | null;
	allergenIds: string[];
	baseIngredientCalories: number | null;
	baseIngredientCharacteristic: Characteristic | null;
	baseIngredientDensity: number;
	baseIngredientId: string | null;
	baseIngredientNutrients: NutrientList.Type;
	baseIngredientScalingFactor: number | null;
	calories: number | null;
	carbonDioxideLabel: Co2Label.Type | null;
	carbonDioxideValue: number;
	categoryIds: string[];
	characteristic: Characteristic | null;
	co2Footprints: Co2List.Type;
	density: number | null;
	hasNutriScore: boolean;
	image: Image.Type | null;
	locked: LockedAttributes.Type;
	namePlural: Translation.Type<string>;
	nameSingular: Translation.Type<string>;
	nutriScore: NutriScore | null;
	nutriScoreCategory: NutriScoreCategory | null;
	nutriScoreCategoryIsSetByUser: boolean;
	nutrients: NutrientList.Type;
	price: number | null;
	products: IngredientProductList.Type;
	scalingFactor: number | null;
	seasonIds: string[];
	selectedCultureCode: string;
	synonyms: SynonymList.Type;
	tagIds: string[];
	unitWeights: UnitWeightList.Type;
};

export function create(): Type {
	return {
		id: null,
		locked: LockedAttributes.create(),
		nameSingular: Translation.create(),
		namePlural: Translation.create(),
		synonyms: SynonymList.create(),
		products: [],
		co2Footprints: Co2List.create(),
		nutrients: NutrientList.create(),
		baseIngredientId: null,
		baseIngredientDensity: 0,
		baseIngredientCalories: null,
		baseIngredientCharacteristic: null,
		baseIngredientNutrients: NutrientList.create(),
		baseIngredientScalingFactor: null,
		characteristic: null,
		density: null,
		calories: null,
		unitWeights: UnitWeightList.create(),
		scalingFactor: null,
		categoryIds: [],
		price: 0,
		tagIds: [],
		allergenIds: [],
		seasonIds: [],
		nutriScore: null,
		nutriScoreCategory: null,
		nutriScoreCategoryIsSetByUser: false,
		hasNutriScore: false,
		image: null,
		carbonDioxideLabel: null,
		carbonDioxideValue: 0,
		selectedCultureCode: '',
	};
}
export function getCharacteristic(state: Type): Characteristic | null {
	return state.characteristic ?? state.baseIngredientCharacteristic;
}

export function setNameSingular(state: Type, input: { cultureCode: string; value: string }): Type {
	return {
		...state,
		nameSingular: Translation.change(state.nameSingular, input.cultureCode, input.value),
	};
}

export function setNamePlural(state: Type, input: { cultureCode: string; value: string }): Type {
	return {
		...state,
		namePlural: Translation.change(state.namePlural, input.cultureCode, input.value),
	};
}

export function lock(state: Type, input: keyof LockedAttributes.Type): Type {
	return {
		...state,
		locked: LockedAttributes.lock(state.locked, input),
	};
}

export function unlock(state: Type, input: keyof LockedAttributes.Type): Type {
	return {
		...state,
		locked: LockedAttributes.unlock(state.locked, input),
	};
}

export function overwriteBaseIngredientValue(
	state: Type,
	input: 'calories' | 'density' | 'characteristic'
): Type {
	let stateKey: keyof Type | null = null;
	if (input === 'calories') stateKey = 'baseIngredientCalories';
	if (input === 'density') stateKey = 'baseIngredientDensity';
	if (input === 'characteristic') stateKey = 'baseIngredientCharacteristic';
	if (!stateKey) return state;

	return {
		...state,
		locked: LockedAttributes.unlock(state.locked, input),
		[input]: state[stateKey],
	};
}

export function restoreBaseIngredientValue(
	state: Type,
	input: 'calories' | 'density' | 'characteristic'
): Type {
	return {
		...state,
		locked: LockedAttributes.lock(state.locked, input),
		[input]: null,
	};
}

export function setDensity(state: Type, value: number): Type {
	return { ...state, density: value };
}

export function setSynonyms(state: Type, input: SynonymList.Type): Type {
	return { ...state, synonyms: input };
}

export function setCharacteristic(state: Type, value: Characteristic): Type {
	return { ...state, characteristic: value };
}

export function setScalingFactor(state: Type, value: number): Type {
	return { ...state, scalingFactor: value };
}

export function setCalories(state: Type, value: number): Type {
	return { ...state, calories: value };
}

export function setTagIds(state: Type, ids: string[]): Type {
	return { ...state, tagIds: ids };
}

export function setSeasonIds(state: Type, ids: string[]): Type {
	return { ...state, seasonIds: ids };
}

export function setAllergenIds(state: Type, input: string[]): Type {
	return { ...state, allergenIds: input };
}

export function setBaseIngredientId(state: Type, id: string | null): Type {
	return { ...state, baseIngredientId: id };
}

export function setNutrients(state: Type, nutrientValues: NutrientList.Type): Type {
	return { ...state, nutrients: nutrientValues };
}

export function setNutriScoreCategoryIsSetByUser(state: Type, isLocked: boolean): Type {
	if (isLocked) {
		return {
			...state,
			nutriScoreCategoryIsSetByUser: false,
			nutriScoreCategory: null,
			hasNutriScore: false,
		};
	}
	return { ...state, nutriScoreCategoryIsSetByUser: true };
}

export function setDensityIsSetByUser(state: Type, isLocked: boolean): Type {
	if (isLocked) {
		return {
			...state,
			density: null,
			locked: {
				...state.locked,
				density: false,
			},
		};
	}
	return {
		...state,
		locked: {
			...state.locked,
			density: true,
		},
	};
}

export function setHasNutriScore(state: Type, hasNutriScore: boolean): Type {
	return { ...state, hasNutriScore: hasNutriScore };
}

export function setNutriScoreCategory(state: Type, category: NutriScoreCategory): Type {
	return { ...state, nutriScoreCategory: category };
}

export function setProducts(state: Type, input: IngredientProductList.Type): Type {
	return { ...state, products: input };
}

export function setPrice(state: Type, input: number | null): Type {
	return { ...state, price: input };
}

export function setSelectedCultureCode(state: Type, cultureCode: string): Type {
	return { ...state, selectedCultureCode: cultureCode };
}

export function setCo2Footprints(state: Type, co2List: Co2List.Type): Type {
	return { ...state, co2Footprints: co2List };
}

export function setCategories(state: Type, input: IngredientCategoryList.Type): Type {
	return { ...state, categoryIds: input.map((e) => e.id) };
}

export function setUnitWeights(state: Type, input: UnitWeightList.Type): Type {
	return { ...state, unitWeights: input };
}

export function load(_state: Type, input: Type): Type {
	return input;
}

export async function save(state: Type, cultureCode: string, currencyCode: string): Promise<Type> {
	if (state.id) {
		const newState = await Ingredient.putToApi(state.id, mapToApi(state, currencyCode));

		return newState
			.map((x) => Ingredient.mapToState(Ingredient.mapFromApi(x), cultureCode))
			.getOrDefault(state);
	}
	const id: string = await Ingredient.postToApi(mapToApi(state, currencyCode));
	if (id !== '') await OrderGuide.postDefaultProductForIngredient(id);
	return {
		...state,
		id: id,
	};
}

export async function translateAndSetName(
	state: Type,
	input:
		| {
				fromCultureCode: string;
				text: string;
				key: 'namePlural' | 'nameSingular';
		  }
		| undefined
): Promise<Type> {
	if (!input) return state;
	if (!input.fromCultureCode) return state;
	if (input.fromCultureCode === state.selectedCultureCode) return state;
	const translatedText: string = await Translation.getTranslationFromApi({
		fromCultureCode: input.fromCultureCode,
		toCultureCode: state.selectedCultureCode,
		text: input.text,
	});

	return {
		...state,
		[input.key]: Translation.change(
			state[input.key],
			state.selectedCultureCode,
			translatedText
		),
	};
}

export async function translateAndSetNames(
	state: Type,
	fromCultureCode: string | undefined
): Promise<Type> {
	if (!fromCultureCode) return state;
	if (fromCultureCode === state.selectedCultureCode) return state;
	const translatedTextSingular: string = await Translation.getTranslationFromApi({
		fromCultureCode: fromCultureCode,
		toCultureCode: state.selectedCultureCode,
		text: state.nameSingular[fromCultureCode],
	});

	const translatedTextPlural: string = await Translation.getTranslationFromApi({
		fromCultureCode: fromCultureCode,
		toCultureCode: state.selectedCultureCode,
		text: state.namePlural[fromCultureCode],
	});

	return {
		...state,
		nameSingular: Translation.change(
			state.nameSingular,
			state.selectedCultureCode,
			translatedTextSingular
		),
		namePlural: Translation.change(
			state.namePlural,
			state.selectedCultureCode,
			translatedTextPlural
		),
	};
}

export async function uploadImage(state: Type, input: FormData | null | undefined): Promise<Type> {
	if (!input) return state;
	return await uploadMedia<Type>(state, input, MEDIACOLLECTION.BaseIngredient);
}

export async function getAndSetSuggestedTags(
	state: Type,
	cultureCode: string | undefined
): Promise<Type> {
	if (!cultureCode) return state;
	const suggestedTags = await TagList.getFromApiSuggested(
		state.nameSingular[cultureCode],
		cultureCode
	);
	const newTagIds: string[] = TagList.applySuggestedTags(suggestedTags, state.tagIds);
	return { ...state, tagIds: newTagIds };
}

function mapToApi(state: Type, currencyCode: string): apiPut.Request & apiPost.Request {
	return {
		...state,
		allergens: AllergenList.mapFromUniqIdList(state.allergenIds),
		categories: state.categoryIds,
		currencyCode: currencyCode,
		imageId: state.image?.id ?? null,
		isAllergenOverridden: !state.locked.allergen,
		isScalingFactorOverridden: !state.locked.scalingFactor,
		isSeasonOverridden: !state.locked.season,
		isTagOverridden: !state.locked.tags,
		isUnitWeightOverridden: !state.locked.unitWeights,
		isCarbonDioxideOverridden: !state.locked.co2,
		namePluralTranslations: state.namePlural,
		nameSingularTranslations: state.nameSingular,
		products: state.products ? state.products.map((e) => e.id) : [],
		seasons: state.seasonIds,
		tags: state.tagIds,
		carbonDioxide: state.co2Footprints,
		nutrientValues: NutrientList.mapToApi(state.nutrients),
		unitWeights: UnitWeightList.mapToApi(state.unitWeights),
	};
}

export function getLinkToBaseIngredient(state: Type): string | undefined {
	if (arePermissionsInUserPermissions([PERMISSIONS.READBASEINGREDIENT])) {
		if (state.baseIngredientId) {
			return `/baseIngredient/detail/${state.baseIngredientId}`;
		}
	}
	return undefined;
}

type CharactersticInput = {
	characteristic: Characteristic;
	dialog: {
		invalidDensity: () => Promise<void>;
		shouldRecalculate: () => Promise<boolean>;
		successfulRecalculation: () => Promise<void>;
	};
};

export async function changeCharacteristic(state: Type, input?: CharactersticInput): Promise<Type> {
	if (!input) return state;
	if (input.characteristic === Characteristic.Fluid) {
		return await changeToFluid(state, input);
	}
	if (input.characteristic === Characteristic.SolidNonQuantifiable) {
		return await changeToSolid(state, input);
	}
	if (input.characteristic === Characteristic.SolidQuantifiable) {
		return await changeToSolid(state, input);
	}
	return state;
}

function getRecalculateCharacteristicRelatedValues(
	state: Type,
	characteristic: Characteristic
): Type {
	const density: number = state.density || state.baseIngredientDensity;
	const factor: number = characteristic === Characteristic.Fluid ? density : 1 / density;

	return {
		...state,
		nutrients: getRecalculatedNutrients(state, factor),
		calories: getRecalculatedCalories(state, factor),
		characteristic: characteristic,
		unitWeights: getRecalculatedUnitWeights(state, factor),
		co2Footprints: getRecalculatedCo2Footprints(state, factor),
		price: getRecalculatedPrice(state, factor),
	};
}

async function changeToFluid(state: Type, input: CharactersticInput): Promise<Type> {
	if (state.density === 0) {
		await input.dialog.invalidDensity();
		return state;
	}
	const recalculate = await input.dialog.shouldRecalculate();
	if (!recalculate) return setCharacteristic(state, input.characteristic);
	input.dialog.successfulRecalculation();
	return getRecalculateCharacteristicRelatedValues(state, input.characteristic);
}

async function changeToSolid(state: Type, input: CharactersticInput): Promise<Type> {
	if (state.characteristic !== Characteristic.Fluid)
		return setCharacteristic(state, input.characteristic);
	const recalculate = await input.dialog.shouldRecalculate();
	if (!recalculate) return setCharacteristic(state, input.characteristic);
	input.dialog.successfulRecalculation();
	return getRecalculateCharacteristicRelatedValues(state, input.characteristic);
}

export function getRecalculatedNutrients(state: Type, factor: number): NutrientList.Type {
	const allNutrients = NutrientList.merge(state.nutrients, state.baseIngredientNutrients);
	const recalculatedNutrients = allNutrients.map((e) => {
		return {
			...e,
			value: e.value * factor,
		};
	});
	return recalculatedNutrients.filter((e) => {
		const nutrient = state.baseIngredientNutrients.find((i) => i.id === e.id);
		if (!nutrient) return true;
		if (nutrient.value === e.value) return false;
		return true;
	});
}

export function getRecalculatedCalories(state: Type, factor: number): number | null {
	return state.calories
		? state.calories * factor
		: state.baseIngredientCalories
		? state.baseIngredientCalories * factor
		: null;
}

export function getRecalculatedUnitWeights(state: Type, factor: number): UnitWeightList.Type {
	return state.unitWeights.map((x) => ({
		...x,
		weight: x.weight * factor,
	}));
}

export function getRecalculatedCo2Footprints(state: Type, factor: number): Co2List.Type {
	return state.co2Footprints.map((x) => ({
		...x,
		value: x.value * factor,
	}));
}

export function getRecalculatedPrice(state: Type, factor: number): number | null {
	return state.price && state.price * factor;
}
